import {
  Badge,
  Box,
  Button,
  Dialog,
  Heading,
  HStack,
  Menu,
  Portal,
  SimpleGrid,
  Spinner,
  Tabs,
  Text,
  VStack
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import EnergyDemandTab from "../components/home-detail/EnergyDemandTab"
import OpportunityTab from "../components/home-detail/OpportunityTab"
import FinancialsTab from "../components/home-detail/FinancialsTab"
import OverviewTab from "../components/home-detail/OverviewTab"
import SolarPotentialTab from "../components/home-detail/SolarPotentialTab"
import CarbonTab from "../components/home-detail/CarbonTab"
import SummaryStatCard from "../components/home-detail/SummaryStatCard"
import { StreetViewImage } from "../components/common/StreetViewImage"
import { useDeleteHome, useMoveHomeToPortfolio, usePortfolios } from "../hooks/usePortfolioQueries"
import { useMarkAsInterested, useOpportunityDetail } from "../hooks/useSourcingQueries"
import { useUserDetails } from "../hooks/useAuthQueries"
import { toaster } from "../toaster"
import type { PortfolioInfo } from "../services/portfolioApi"
import {
  getFinancials,
  getHomeDetail,
} from "../services/portfolioApi"
import {
  calculateNetGridImport,
  calculateSolarGeneration,
  calculateTotalElectricityDemand,
  getAvailableScenarios,
  getLatestData,
  getSelectedEnergyEstimate,
  getZeroBillPotential
} from "../utils/homeDetailCalculations"
import { getNetHeatPumpCost } from "../utils/financials"
import { getStatusColor, getStatusDisplayText } from "../utils/propertyUtils"

const HomeDetail: React.FC = () => {
  const { homeId } = useParams<{ homeId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const homeIdNum = homeId ? parseInt(homeId, 10) : 0
  const deleteHomeMutation = useDeleteHome()
  const moveHomeMutation = useMoveHomeToPortfolio()
  const markAsInterestedMutation = useMarkAsInterested()
  const { data: portfolios = [] } = usePortfolios()

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false)
  const [targetPortfolio, setTargetPortfolio] = useState<PortfolioInfo | null>(null)

  const [searchParams] = useSearchParams()
  const opportunityId = searchParams.get("opportunityId")
  const opportunityIdNum = opportunityId ? parseInt(opportunityId, 10) : 0
  const hasHistory = useRef(window.history.length > 1 && document.referrer !== "")
  const fromPath: string | undefined = (location.state as any)?.from

  const { data: userDetails } = useUserDetails()
  const isPropertyInvestment =
    userDetails?.data?.organisation?.organisationType === "property_investment"
  const homesPath = isPropertyInvestment ? "/portfolio" : "/my-homes"

  const handleBack = () => {
    if (fromPath) {
      navigate(fromPath)
    } else if (hasHistory.current) {
      navigate(-1)
    } else {
      navigate(fromPath ?? (opportunityIdNum > 0 ? "/opportunities" : homesPath))
    }
  }

  // Get home details with heat loss and solar data
  const {
    data: homeDetailData,
    isLoading: homeDetailLoading,
    error: homeDetailError,
    refetch: refetchHomeDetail,
  } = useQuery({
    queryKey: ["homeDetail", homeIdNum],
    queryFn: () => getHomeDetail(homeIdNum),
    enabled: !!localStorage.getItem("token") && homeIdNum > 0
  })

  const home = homeDetailData?.home

  const { data: opportunityDetail } = useOpportunityDetail(opportunityIdNum)
  const isInKanban = opportunityIdNum > 0 && (opportunityDetail?.kanbanStage ?? null) !== null
  const hasFailedOrMissingAnalysis =
    home?.status === "failed" || home?.status === "missing_data"
  const canRemoveFromPortfolio =
    isInKanban || (home?.portfolio != null && hasFailedOrMissingAnalysis)
  const heatLossCalculations = homeDetailData?.heatLossCalculations || []
  const solarAnalyses = homeDetailData?.solarAnalyses || []
  const energyCalculations = homeDetailData?.energyCalculations || []
  // Access energyEstimates (camelCase after humps transformation)
  const energyEstimates = homeDetailData?.energyEstimates || []
  const isEnergyEstimatesEnabled =
    process.env.REACT_APP_NEW_ENERGY_ENABLE === "1"
  const shouldUseEnergyEstimates =
    isEnergyEstimatesEnabled && energyEstimates.length > 0
  const isFinanceEnabled =
    (process.env.REACT_APP_FINANCE_ENABLED === "1" ||
      process.env.REACT_APP_FINANCE_ENABLED === "true") &&
    energyEstimates.length > 0

  const availableScenarios = getAvailableScenarios(heatLossCalculations)
  const [selectedScenario, setSelectedScenario] = useState<string>(
    availableScenarios.includes("default")
      ? "default"
      : availableScenarios[0] || ""
  )
  const [activeTab, setActiveTab] = useState<string>(
    opportunityIdNum > 0 ? "opportunity" : "financials"
  )

  // Sync selected scenario when available scenarios change
  useEffect(() => {
    if (
      availableScenarios.length > 0 &&
      !availableScenarios.includes(selectedScenario)
    ) {
      setSelectedScenario(
        availableScenarios.includes("default")
          ? "default"
          : availableScenarios[0] || ""
      )
    }
  }, [availableScenarios, selectedScenario])

  const { latestHeatLoss, latestEnergy, latestSolar } = getLatestData(
    heatLossCalculations,
    energyCalculations,
    solarAnalyses,
    selectedScenario
  )

  const selectedEnergyEstimate = getSelectedEnergyEstimate(
    energyEstimates,
    shouldUseEnergyEstimates
  )

  const totalElectricityDemand = calculateTotalElectricityDemand({
    latestHeatLoss,
    latestEnergy,
    shouldUseEnergyEstimates,
    selectedEnergyEstimate
  })

  const solarGeneration = calculateSolarGeneration(latestSolar)

  const netGridImport = calculateNetGridImport(
    totalElectricityDemand,
    solarGeneration
  )

  const heatLossCop =
    latestHeatLoss && (latestHeatLoss as any)?.copUsed
      ? parseFloat(((latestHeatLoss as any)?.copUsed as string) || "0")
      : undefined

  const { data: financials } = useQuery({
    queryKey: ["financials", homeIdNum],
    queryFn: () => getFinancials(homeIdNum),
    enabled:
      !!localStorage.getItem("token") &&
      homeIdNum > 0 &&
      isFinanceEnabled
  })

  const handleConfirmRemove = async () => {
    try {
      await deleteHomeMutation.mutateAsync(homeIdNum)
      queryClient.invalidateQueries({ queryKey: ["homeDetail", homeIdNum] })
      toaster.create({
        title: "Removed from portfolio",
        description: `${home?.fullAddress} has been removed from your portfolio.`,
        type: "success",
        duration: 4000,
      })
      navigate(fromPath ?? (opportunityIdNum > 0 ? "/opportunities" : homesPath))
    } catch (error) {
      console.error("Error removing home:", error)
      toaster.create({
        title: "Removal failed",
        description: "There was an error removing this home from your portfolio. Please try again.",
        type: "error",
        duration: 4000,
      })
    } finally {
      setIsRemoveDialogOpen(false)
    }
  }

  const handleSelectMoveTarget = (portfolio: PortfolioInfo) => {
    setTargetPortfolio(portfolio)
    setIsMoveDialogOpen(true)
  }

  const handleConfirmMove = async () => {
    if (!targetPortfolio) return
    try {
      await moveHomeMutation.mutateAsync({ homeId: homeIdNum, targetPortfolioId: targetPortfolio.id, opportunityId: opportunityIdNum > 0 ? opportunityIdNum : undefined })
      toaster.create({
        title: "Moved to portfolio",
        description: `${home?.fullAddress} has been moved to ${targetPortfolio.name}.`,
        type: "success",
        duration: 4000,
      })
      navigate(fromPath ?? (opportunityIdNum > 0 ? "/opportunities" : homesPath))
    } catch (error) {
      console.error("Error moving home:", error)
    } finally {
      setIsMoveDialogOpen(false)
      setTargetPortfolio(null)
    }
  }

  if (homeDetailLoading) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Box bg="white" p={8} borderRadius="lg" textAlign="center">
          <Spinner size="lg" color="#32544A" />
          <Text mt={4} color="gray.500">Loading home details...</Text>
        </Box>
      </Box>
    )
  }

  if (homeDetailError || !home) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <VStack gap={4} align="stretch">
          <Heading size="xl">
            {homeDetailError ? "Could not load data" : "Home not found"}
          </Heading>
          <Text color="gray.600">
            {homeDetailError
              ? "There was a problem loading this home. Check your connection and try again."
              : "The requested home could not be found."}
          </Text>
          <HStack gap={3}>
            <Button variant="ghost" onClick={handleBack}>
              ← Back to the list
            </Button>
            {homeDetailError && (
              <Button variant="outline" onClick={() => refetchHomeDetail()}>
                Retry
              </Button>
            )}
          </HStack>
        </VStack>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Full width button row */}
        <HStack width="100%" justify="space-between" align="center">
          <Button variant="ghost" onClick={handleBack}>
            ← Back to the list
          </Button>
          <HStack gap={2}>
            {/* Mark as Interested – shown when viewing an opportunity not yet in kanban */}
            {opportunityIdNum > 0 && !isInKanban && (
              <Button
                variant="outline"
                size="sm"
                colorPalette="green"
                onClick={async () => {
                  try {
                    await markAsInterestedMutation.mutateAsync(opportunityIdNum)
                    toaster.create({
                      title: "Marked as interested",
                      description: `${home?.fullAddress} has been added to your pipeline.`,
                      type: "success",
                      duration: 4000,
                    })
                  } catch {
                    // mutation handles its own error state
                  }
                }}
                disabled={markAsInterestedMutation.isPending}
              >
                {markAsInterestedMutation.isPending ? <Spinner size="sm" /> : "Mark as Interested"}
              </Button>
            )}

            {/* Move to portfolio */}
            {isInKanban && portfolios.length > 1 && (
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button variant="outline" size="sm">
                    Move to portfolio
                  </Button>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>
                      {portfolios
                        .filter((p) => p.id !== (opportunityIdNum > 0 ? opportunityDetail?.portfolio : home?.portfolio))
                        .map((p) => (
                          <Menu.Item
                            key={p.id}
                            value={p.id.toString()}
                            onClick={() => handleSelectMoveTarget(p)}
                          >
                            {p.name}
                          </Menu.Item>
                        ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            )}

            {/* Remove from portfolio */}
            {canRemoveFromPortfolio && (
              <Button
                variant="outline"
                size="sm"
                colorPalette="red"
                onClick={() => setIsRemoveDialogOpen(true)}
                disabled={deleteHomeMutation.isPending}
              >
                Remove from portfolio
              </Button>
            )}
          </HStack>
        </HStack>

        {/* Remove from portfolio confirmation dialog */}
        <Dialog.Root open={isRemoveDialogOpen} onOpenChange={({ open }) => setIsRemoveDialogOpen(open)}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Remove from portfolio</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Text>
                    Are you sure you want to remove <strong>{home.fullAddress}</strong> from this portfolio?
                  </Text>
                </Dialog.Body>
                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline">Cancel</Button>
                  </Dialog.ActionTrigger>
                  <Button
                    colorPalette="red"
                    onClick={handleConfirmRemove}
                    disabled={deleteHomeMutation.isPending}
                  >
                    {deleteHomeMutation.isPending ? <Spinner size="sm" /> : "Remove"}
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

        {/* Move to portfolio confirmation dialog */}
        <Dialog.Root open={isMoveDialogOpen} onOpenChange={({ open }) => { if (!open) { setIsMoveDialogOpen(false); setTargetPortfolio(null) } }}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Move to portfolio</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Text>
                    Move <strong>{home.fullAddress}</strong> to <strong>{targetPortfolio?.name}</strong>?
                  </Text>
                </Dialog.Body>
                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline" onClick={() => { setIsMoveDialogOpen(false); setTargetPortfolio(null) }}>
                      Cancel
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button
                    colorPalette="green"
                    onClick={handleConfirmMove}
                    disabled={moveHomeMutation.isPending}
                  >
                    {moveHomeMutation.isPending ? <Spinner size="sm" /> : "Move"}
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

        {/* Image and content row */}
        <HStack
          gap={{ base: 4, md: 6 }}
          align="center"
          flexDirection={{ base: "column", md: "row" }}
        >
          {/* Image on the left */}
          {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
            <Box flex={{ base: "0 0 100%", md: "0 1 auto" }} minW={0} maxW={{ md: "280px", lg: "300px" }}>
              <StreetViewImage
                imageUrl={home.imageUrl}
                address={home.fullAddress}
                svSize="600x400"
                alt={`Street view of ${home.fullAddress}`}
                borderRadius="md"
                width={{ base: "100%", md: "100%" }}
                maxW="100%"
                maxH={{ base: "150px", md: "200px", lg: "240px" }}
                border="1px solid"
                borderColor="gray.200"
                objectFit="cover"
              />
            </Box>
          )}

          {/* Heading and cards on the right */}
          <VStack align="start" gap={0} flex="1" minW={0}>
            <Heading size={{ base: "2xl", md: "3xl" }}>
              {home.fullAddress}
            </Heading>
            <HStack flexWrap="wrap" gap={2} mt={2}>
              {home.features.propertyType && home.features.builtForm && (
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                  {home.features.propertyType || ""},{" "}
                  {home.features.builtForm || ""}
                </Text>
              )}
              <Badge colorPalette={getStatusColor(home.status)} fontSize="sm">
                {getStatusDisplayText(home.status)}
              </Badge>
              {latestHeatLoss && latestEnergy && latestSolar && (() => {
                const zeroBillPotential = getZeroBillPotential(netGridImport)
                return (
                  <Badge colorPalette={zeroBillPotential.color} variant="subtle" fontSize="sm">
                    {zeroBillPotential.label} Zero Bill Potential
                  </Badge>
                )
              })()}
            </HStack>

            {/* Place cards here with numbers overview of the home */}
            {home.status !== "missing_data" && home.status !== "failed" && heatLossCalculations.length > 0 && (
              <SimpleGrid
                columns={{ base: 1, sm: 2, lg: 3 }}
                minChildWidth="230px"
                mt={6}
                gap={{ base: 3, md: 4 }}
                width="100%"
                maxW="100%"
              >
                {isFinanceEnabled && financials ? (
                  <>
                    {(() => {
                      const solarPvCost = financials.solarPvCost || 0
                      const batteryCost = financials.batteryCost || 0
                      const netHeatPumpCost = getNetHeatPumpCost(
                        financials.heatPumpCost,
                        financials.heatPumpGrant
                      )
                      const totalUpgradeCosts =
                        solarPvCost + batteryCost + netHeatPumpCost
                      const formatCurrency = (value: number) =>
                        `£${Math.round(value).toLocaleString()}`
                      const totalSavings = financials.annualSavings
                      const roi = financials.roi
                      const paybackPeriod = financials.paybackPeriod

                      return (
                        <>
                          <SummaryStatCard
                            title="Total cost of upgrades"
                            value={formatCurrency(totalUpgradeCosts)}
                            subline={`${formatCurrency(totalSavings)} of annual savings` }
                            bgColor="gray.100"
                          />
                          <SummaryStatCard
                            title="Retrofit ROI %"
                            value={roi != null ? `${roi.toFixed(1)}%` : "0%"}
                            subline={
                              paybackPeriod != null
                                ? `${paybackPeriod} years payback`
                                : "N/A"
                            }
                            bgColor="gray.100"
                          />
                        </>
                      )
                    })()}
                  </>
                ) : (
                  <>
                    <SummaryStatCard
                      title="Total electricity demand"
                      value={totalElectricityDemand.toString()}
                      subline="kWh/year"
                      bgColor="gray.100"
                    />
                    <SummaryStatCard
                      title="Solar generation estimate"
                      value={solarGeneration.toString()}
                      subline="kWh/year"
                      bgColor="gray.100"
                    />
                  </>
                )}
                <SummaryStatCard
                  title="Net grid import"
                  value={netGridImport.toString()}
                  subline="kWh/year"
                  bgColor="gray.100"
                />
              </SimpleGrid>
            )}
          </VStack>
        </HStack>

        {home.status === "missing_data" || home.status === "failed" || heatLossCalculations.length === 0 ? (
          <Box bg="white" p={12} borderRadius="lg" textAlign="center">
            <VStack gap={4}>
              <Heading size="xl" color="gray.700">
                {home.status === "failed" ? "Analysis failed" : "Analysis pending"}
              </Heading>
              <Text fontSize="md" color="gray.600" maxW="md">
                {home.status === "failed"
                  ? "We were unable to complete the analysis for this home. Try re-adding the address or contact support if the problem persists."
                  : "Complete analysis data isn't available for this home yet. Check back after the next data sync."}
              </Text>
            </VStack>
          </Box>
        ) : (
          <Tabs.Root value={activeTab} onValueChange={(e) => {
            const newValue = e?.value ?? e;
            setActiveTab(newValue);
          }} size="lg">
            <Tabs.List overflowX="auto" style={{ WebkitOverflowScrolling: "touch" }}>
              {opportunityIdNum > 0 && (
                <Tabs.Trigger value="opportunity" flexShrink={0}>Opportunity</Tabs.Trigger>
              )}
              {isFinanceEnabled && (
                <Tabs.Trigger value="financials" flexShrink={0}>Financials</Tabs.Trigger>
              )}
              <Tabs.Trigger value="overview" flexShrink={0}>Building details</Tabs.Trigger>
              <Tabs.Trigger value="heat-loss" flexShrink={0}>Energy demand</Tabs.Trigger>
              <Tabs.Trigger value="solar-potential" flexShrink={0}>Solar potential</Tabs.Trigger>
              <Tabs.Trigger value="carbon" flexShrink={0}>Carbon</Tabs.Trigger>
            </Tabs.List>

            {opportunityIdNum > 0 && (
              <Tabs.Content value="opportunity">
                <OpportunityTab opportunityId={opportunityIdNum} />
              </Tabs.Content>
            )}

            {isFinanceEnabled && (
              <Tabs.Content value="financials">
                <FinancialsTab homeId={homeIdNum} />
              </Tabs.Content>
            )}

            <Tabs.Content value="overview">
              <OverviewTab
                home={home}
                heatLossCalculations={heatLossCalculations}
                energyCalculations={energyCalculations}
                solarAnalyses={solarAnalyses}
                bedrooms={opportunityDetail?.scrapedProperty?.bedrooms ?? null}
              />
            </Tabs.Content>

            <Tabs.Content value="heat-loss">
              <EnergyDemandTab
                heatLossCalculations={heatLossCalculations}
                home={home}
                selectedScenario={selectedScenario}
                onScenarioChange={setSelectedScenario}
                energyCalculations={energyCalculations}
                energyEstimates={energyEstimates}
                shouldUseEnergyEstimates={shouldUseEnergyEstimates}
                copUsed={heatLossCop}
                homeId={homeIdNum}
                isActive={activeTab === "heat-loss"}
              />
            </Tabs.Content>

            <Tabs.Content value="solar-potential">
              <SolarPotentialTab
                solarAnalyses={solarAnalyses}
                homeId={homeIdNum}
                home={home}
                isActive={activeTab === "solar-potential"}
              />
            </Tabs.Content>

            <Tabs.Content value="carbon">
              <CarbonTab homeId={homeIdNum} isActive={activeTab === "carbon"} />
            </Tabs.Content>
          </Tabs.Root>
        )}
      </VStack>
    </Box>
  )
}

export default HomeDetail
